import React, { useContext, lazy, Suspense, useState, useEffect, useRef } from 'react';
import { ClientOnly, getRouteApi } from '@tanstack/react-router';
import { DestinationContext } from '../../../contexts/DestinationContext';
import styles from './LocationSection.module.scss';
import MapSkeleton from '../../utils/Maps/MapSkeleton';

const routeApi = getRouteApi('/');

const LazyLeafletMap = lazy(() => import('../../utils/Maps/LeafletMap'));

// No timezone field on the destination record. Real-world timezones don't
// follow pure longitude math (e.g. Kenya at ~37°E is GMT+3, not the
// longitude/15 estimate of GMT+2) — so look the country up against its
// actual standard UTC offset first, falling back to the longitude estimate
// only for countries not in the list.
const COUNTRY_UTC_OFFSETS = {
  kenya: 3,
  tanzania: 3,
  uganda: 3,
  rwanda: 2,
  burundi: 2,
  ethiopia: 3,
  somalia: 3,
  'south africa': 2,
  namibia: 2,
  botswana: 2,
  zambia: 2,
  zimbabwe: 2,
  mozambique: 2,
  malawi: 2,
  madagascar: 3,
};

const formatOffset = (offset) => {
  if (offset === 0) return 'GMT';
  return `GMT${offset > 0 ? '+' : ''}${offset}`;
};

const timezoneFor = (country, longitude) => {
  const known = country && COUNTRY_UTC_OFFSETS[country.trim().toLowerCase()];
  if (typeof known === 'number') return formatOffset(known);
  if (typeof longitude !== 'number') return null;
  return formatOffset(Math.round(longitude / 15));
};

const LocationSection = () => {
  const destination = useContext(DestinationContext);
  const { countriesList } = routeApi.useLoaderData();
  const { latitude, longitude } = destination?.location || {};
  const details = destination?.locationDetails;
  // `destination.country` is an ObjectId reference, not a display name —
  // resolve it against the countries list to get the real country name.
  const resolvedCountryName = (countriesList || []).find(
    (c) => c._id === destination?.country
  )?.name;
  const country = details?.countyRegion || resolvedCountryName;
  const timezone = timezoneFor(country, longitude);

  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const mapContainerRef = useRef(null);

  useEffect(() => {
    if (shouldLoadMap) return;
    const el = mapContainerRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoadMap(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoadMap(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [shouldLoadMap]);

  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.header}>
          <div className={styles.eyebrow}>Location</div>
          <h2 className={styles.heading}>
            Where is the{' '}
            <span className={styles.accent}>
              {details?.reserveName || destination?.name}
            </span>
            ?
          </h2>
        </div>

        <div className={styles.map} ref={mapContainerRef}>
          <div className={styles.mapInner}>
            {latitude && longitude && shouldLoadMap ? (
              <ClientOnly
                fallback={<MapSkeleton dark label="Loading reserve map…" />}
              >
                <Suspense
                  fallback={<MapSkeleton dark label="Initializing map…" />}
                >
                  <LazyLeafletMap
                    latitude={latitude}
                    longitude={longitude}
                    zoom={7}
                    darkSkeleton
                  />
                </Suspense>
              </ClientOnly>
            ) : (
              <MapSkeleton dark label="Loading reserve map…" />
            )}
          </div>

          <div className={styles.card}>
            <div className={styles.factsTitle}>
              {details?.reserveName || 'The Reserve'}
            </div>
            <div className={styles.divider} />
            <dl>
              {country && (
                <>
                  <dt>Country</dt>
                  <dd>{country}</dd>
                </>
              )}
              {details?.coordinatesText ? (
                <>
                  <dt>Coordinates</dt>
                  <dd>{details.coordinatesText}</dd>
                </>
              ) : latitude && longitude ? (
                <>
                  <dt>Coordinates</dt>
                  <dd>
                    {latitude.toFixed(2)}°, {longitude.toFixed(2)}°
                  </dd>
                </>
              ) : null}
              {timezone && (
                <>
                  <dt>Time zone</dt>
                  <dd>{timezone}</dd>
                </>
              )}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
